/**
 * Fogatti Cart Coupon Controller
 *
 * Dependencies: fgt-coupon-utils.js loaded before this file.
 * Reads coupon JSON generated inside each line-item, resolves its ACTUAL
 * variant, and updates the existing cart offer markup.
 * Does not apply discounts or change Shopify-calculated totals.
 */
(() => {
  'use strict';

  // Prevent duplicate global observers/timers if this asset is loaded twice.
  if (window.FogattiCartCoupon) return;

  const LINE_SELECTOR = 'line-item[data-fgt-cart-line-item]';
  const CARD_SELECTOR = '[data-fgt-cart-coupon]';
  const activeTimers = new Set();
  const processedNodes = new WeakMap();
  let observer = null;
  let timerId = null;

  function show(node, visible) {
    if (!node) return;
    node.hidden = !visible;
    // Some Fogatti / theme CSS uses display: ... !important, overriding [hidden].
    if (visible) node.style.removeProperty('display');
    else node.style.setProperty('display', 'none', 'important');
  }

  function getAppliedCodes(lineItem) {
    try {
      const parsed = JSON.parse(lineItem.dataset.fgtAppliedCodes || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[Fogatti Cart Coupon] Invalid applied-code data.', error);
      return [];
    }
  }

  function isApplied(lineItem, code) {
    const normalized = String(code || '').trim().toLowerCase();
    return getAppliedCodes(lineItem).some(
      (applied) => String(applied).trim().toLowerCase() === normalized
    );
  }

  function getCoupon(lineItem) {
    if (!window.FogattiCoupon) return null;
    const data = window.FogattiCoupon.loadData({ root: lineItem });
    if (!data) return null;
    const map = window.FogattiCoupon.buildMap(data);
    return window.FogattiCoupon.getByVariantId(
      map,
      lineItem.dataset.fgtVariantId
    );
  }

  function formatTime(ms) {
    const secondsTotal = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(secondsTotal / 86400);
    const hours = Math.floor((secondsTotal % 86400) / 3600);
    const minutes = Math.floor((secondsTotal % 3600) / 60);
    const seconds = secondsTotal % 60;
    const clock = [hours, minutes, seconds]
      .map((number) => String(number).padStart(2, '0'))
      .join(':');
    return days ? `${days}d ${clock}` : clock;
  }

  function updateTimer(lineItem, now = Date.now()) {
    const card = lineItem.querySelector(CARD_SELECTOR);
    if (!card) {
      activeTimers.delete(lineItem);
      return;
    }
    const end = Number(card.dataset.fgtCartCouponEnd);
    if (!Number.isFinite(end) || end <= 0 || end <= now) {
      show(card, false);
      activeTimers.delete(lineItem);
      notify();
      return;
    }
    const value = card.querySelector('[data-fgt-cart-countdown-value]');
    if (value) value.textContent = formatTime(end - now);
  }

  function updateLine(lineItem) {
    const card = lineItem.querySelector(CARD_SELECTOR);
    if (!card) return;

    activeTimers.delete(lineItem);
    show(card, false);
    card.removeAttribute('data-fgt-cart-coupon-end');

    const coupon = getCoupon(lineItem);
    if (!coupon?.enabled) {
      processedNodes.set(lineItem, lineItem.dataset.fgtVariantId);
      return;
    }

    // Resolver tests field presence; cart also needs a parseable future date.
    const end = Date.parse(coupon.countdown);
    if (!Number.isFinite(end) || end <= Date.now()) {
      processedNodes.set(lineItem, lineItem.dataset.fgtVariantId);
      return;
    }

    const event = card.querySelector('[data-fgt-cart-coupon-event]');
    const discount = card.querySelector('[data-fgt-cart-coupon-discount]');
    const code = card.querySelector('[data-fgt-cart-coupon-code]');
    const timer = card.querySelector('[data-fgt-cart-coupon-countdown]');
    const apply = card.querySelector('[data-fgt-cart-coupon-apply]');
    const note = card.querySelector('[data-fgt-cart-coupon-note]');

    if (event) event.textContent = coupon.eventName;
    if (discount) discount.textContent = `Save $${coupon.discount}`;
    if (code) code.textContent = coupon.discountCode;

    const applied = isApplied(lineItem, coupon.discountCode);
    if (apply) {
      const base = (lineItem.dataset.fgtDiscountBase || '/').replace(/\/?$/, '/');
      const redirect = lineItem.dataset.fgtCartUrl || '/cart';
      apply.href = `${base}discount/${encodeURIComponent(coupon.discountCode)}?redirect=${encodeURIComponent(redirect)}`;
      apply.dataset.fgDiscountCode = coupon.discountCode;
      apply.dataset.fgDiscountIdleLabel = applied ? 'Applied' : 'Apply code';
      apply.classList.toggle('is-applied', applied);
      apply.textContent = applied ? 'Applied' : 'Apply code';
      apply.setAttribute('aria-label', applied
        ? `Coupon code ${coupon.discountCode} applied`
        : `Apply coupon code ${coupon.discountCode}`);
      if (applied) {
        apply.setAttribute('aria-disabled', 'true');
        apply.tabIndex = -1;
      } else {
        apply.removeAttribute('aria-disabled');
        apply.removeAttribute('tabindex');
      }
    }
    if (note) {
      note.textContent = applied
        ? 'Included in the estimated total. Final eligibility is confirmed at checkout.'
        : 'Apply now to update the estimated total. Final eligibility is confirmed at checkout.';
    }

    card.setAttribute('aria-label', `$${coupon.discount} off. Coupon code ${coupon.discountCode}`);
    card.dataset.fgtCartCouponEnd = String(end);
    show(timer, true);
    show(card, true);
    activeTimers.add(lineItem);
    updateTimer(lineItem);
    processedNodes.set(lineItem, lineItem.dataset.fgtVariantId);
  }

  function notify() {
    const visibleCount = [...document.querySelectorAll(`${LINE_SELECTOR} ${CARD_SELECTOR}`)]
      .filter((card) => !card.hidden).length;
    document.dispatchEvent(new CustomEvent('fgt:cart-coupons-updated', {
      detail: { visibleCount, hasOffers: visibleCount > 0 }
    }));
  }

  function refresh(scope = document) {
    if (!window.FogattiCoupon) {
      console.warn('[Fogatti Cart Coupon] Load fgt-coupon-utils.js BEFORE fgt-cart-coupon.js.');
      return;
    }
    const lines = [];
    if (scope instanceof Element && scope.matches(LINE_SELECTOR)) lines.push(scope);
    scope.querySelectorAll?.(LINE_SELECTOR).forEach((line) => lines.push(line));
    lines.forEach(updateLine);
    notify();
  }

  // The theme can call this after its cart-section update, if it has such an event.
  window.FogattiCartCoupon = { refresh };

  function start() {
    refresh();
    if (timerId === null) {
      timerId = window.setInterval(() => {
        const now = Date.now();
        for (const line of activeTimers) {
          if (!line.isConnected) {
            activeTimers.delete(line);
            continue;
          }
          updateTimer(line, now);
        }
      }, 1000);
    }

    // Only reacts to newly inserted line-item elements/changed variant IDs.
    // No coupon DOM mutations are observed, so it won't loop on text updates.
    if (!observer && document.body) {
      observer = new MutationObserver((mutations) => {
        const newLines = new Set();
        for (const mutation of mutations) {
          if (mutation.type === 'attributes') {
            if (mutation.target.matches?.(LINE_SELECTOR)) newLines.add(mutation.target);
            continue;
          }
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches?.(LINE_SELECTOR)) newLines.add(node);
            node.querySelectorAll?.(LINE_SELECTOR).forEach((line) => newLines.add(line));
          }
        }
        if (!newLines.size) return;
        newLines.forEach((line) => {
          if (processedNodes.get(line) !== line.dataset.fgtVariantId || !line.querySelector(CARD_SELECTOR)?.dataset.fgtCartCouponEnd) {
            updateLine(line);
          }
        });
        notify();
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-fgt-variant-id']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
